import { useEffect, useState, useCallback } from 'react';
import { authActions } from '../../stores/authStore';
import { formatDateTime } from '../../utils/dateUtils';

/**
 * Passkeys management card for My Profile (Playbook PR 9).
 *
 * Lets the user enrol new passkeys (biometric / security key / phone), give
 * them friendly names, see when they were last used, and remove them. The
 * actual WebAuthn ceremony is delegated to `@simplewebauthn/browser` —
 * dynamically imported so the ~30KB lib stays out of the main bundle for
 * users who never open this card.
 *
 * Feature-detects `window.PublicKeyCredential` — if the browser doesn't
 * support WebAuthn (rare in 2026, mostly very old corporate browsers) the
 * card explains the limitation rather than offering a button that does
 * nothing.
 */

interface Passkey {
  id: string;
  deviceName: string;
  lastUsedAt: string | null;
  createdAt: string;
  transports: string[];
}

const supportsWebAuthn = typeof window !== 'undefined' && !!window.PublicKeyCredential;

export function PasskeysCard({
  showToast,
}: {
  showToast: (msg: string, t?: 'success' | 'error' | 'info') => void;
}) {
  const [passkeys, setPasskeys] = useState<Passkey[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const reload = useCallback(async () => {
    try {
      const list = await authActions.listPasskeys();
      setPasskeys(list);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);
  useEffect(() => { if (supportsWebAuthn) reload(); }, [reload]);

  async function addPasskey() {
    setBusy(true);
    setError('');
    try {
      const { startRegistration } = await import('@simplewebauthn/browser');
      const options = await authActions.startPasskeyRegistration();
      // The library types are strict — our store has it as `unknown` so we
      // hand it through; the lib validates the actual shape.
      const response = await startRegistration({ optionsJSON: options as Parameters<typeof startRegistration>[0]['optionsJSON'] });
      const deviceName = guessDeviceName();
      await authActions.finishPasskeyRegistration({ response, deviceName });
      showToast('Passkey added', 'success');
      reload();
    } catch (err) {
      const e = err as Error;
      // Common case: the user dismissed the browser prompt. Don't show
      // that as an error — they cancelled deliberately.
      if (e.name === 'NotAllowedError' || /cancel/i.test(e.message)) {
        return;
      }
      setError(e.message || 'Failed to add passkey');
    } finally {
      setBusy(false);
    }
  }

  async function rename(id: string) {
    if (renameValue.trim().length === 0) return;
    setBusy(true);
    try {
      await authActions.renamePasskey(id, renameValue.trim());
      setRenamingId(null);
      setRenameValue('');
      showToast('Passkey renamed', 'success');
      reload();
    } catch (err) {
      showToast(`Failed to rename: ${(err as Error).message}`, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string, name: string) {
    if (!window.confirm(`Remove "${name}"? You won't be able to sign in with this passkey any more.`)) return;
    setBusy(true);
    try {
      await authActions.deletePasskey(id);
      showToast('Passkey removed', 'success');
      reload();
    } catch (err) {
      showToast(`Failed to remove: ${(err as Error).message}`, 'error');
    } finally {
      setBusy(false);
    }
  }

  if (!supportsWebAuthn) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Passkeys</h2>
        <p className="text-sm text-gray-500">
          Your browser doesn't support passkeys. Try the latest Chrome, Edge, Firefox, or Safari to use
          biometric / security-key sign-in here.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Passkeys</h2>
          <p className="text-sm text-gray-500 mt-1">
            Sign in with Face ID, Touch ID, Windows Hello, or a hardware security key — no password
            needed. Each device gets its own passkey; remove a passkey when you stop using a device.
          </p>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mb-3">{error}</div>}

      {passkeys === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : passkeys.length === 0 ? (
        <p className="text-sm text-gray-500 mb-4">No passkeys registered yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 mb-4 -mx-2">
          {passkeys.map((p) => (
            <li key={p.id} className="px-2 py-3">
              {renamingId === p.id ? (
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    placeholder="Device name"
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  />
                  <button
                    onClick={() => rename(p.id)}
                    disabled={busy}
                    className="px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50"
                    style={{ background: 'var(--color-accent)' }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setRenamingId(null); setRenameValue(''); }}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{p.deviceName}</div>
                    <div className="text-xs text-gray-500">
                      Added {formatDateTime(p.createdAt)}
                      {p.lastUsedAt && <> · Last used {formatDateTime(p.lastUsedAt)}</>}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => { setRenamingId(p.id); setRenameValue(p.deviceName); }}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      Rename
                    </button>
                    <button
                      onClick={() => remove(p.id, p.deviceName)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={addPasskey}
        disabled={busy}
        className="px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50"
        style={{ background: 'var(--color-accent)' }}
      >
        {busy ? 'Working…' : 'Add a passkey'}
      </button>
    </div>
  );
}

/**
 * Best-effort guess at a friendly default device name from the user-agent.
 * The user can rename it from the list afterward; this is just so the new
 * row doesn't say "New passkey" forever.
 */
function guessDeviceName(): string {
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iPhone / iPad';
  if (/Mac OS X/i.test(ua)) return 'Mac';
  if (/Android/i.test(ua)) return 'Android device';
  if (/Windows/i.test(ua)) return 'Windows PC';
  if (/Linux/i.test(ua)) return 'Linux PC';
  return 'New passkey';
}
